import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { differenceInSeconds, format, parse } from 'date-fns';
import ToolTipIcon from '../ToolTipIcon/ToolTipIcon';
import SSTable from '../SSTable/SSTable';
import SSColumn from '../SSTable/SSColumn';
import ToolTip from '../ToolTipIcon/ToolTip';
import './datePicker.css';

export default class CampaignScheduleEntriesTable extends Component {
  getMaxDate = () => {
    const { data } = this.props;

    return data.find(
      item => differenceInSeconds(parse(item.date, 'yyyyMMdd', new Date()), new Date()) < 0,
    )
      ? null
      : new Date();
  };

  getPosition = (entry, index) => (
    <span className="circle circle-md poppins-bold font-700 text-dark-night">{index + 1}</span>
  );

  getName = entry => (
    <div className="entry-name">
      <ToolTip toolText={`Folder: ${entry.campaignParent}`}>
        <span className="poppins-regular text-dark-night !text-md">{entry.campaignName}</span>
      </ToolTip>
    </div>
  );

  getDescription = entry => {
    const { campaignDescription } = entry;
    let { campaignTags = '' } = entry || {};

    if (!campaignTags || typeof campaignTags !== 'string') {
      campaignTags = '';
    }

    const tags = campaignTags ? campaignTags.split(',').filter(Boolean) : [];

    return (
      <div
        data-test="entry-description-container"
        className="entry-description flex flex-col gap-1"
      >
        {campaignDescription && <div className="entry-description-text">{campaignDescription}</div>}
        {tags.length > 0 && (
          <div data-test="entry-tags" className="flex flex-wrap gap-1">
            {tags.map(tag => (
              <span
                key={tag}
                className="inline-flex items-center rounded-md bg-orange-50 px-2 py-1 text-xs font-medium text-sunrise inset-ring inset-ring-orange-600/20"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  getDate = (entry, index) => {
    const {
      auth: {
        user: {
          permissions: { Writer },
        },
      },
      onChangeDate,
    } = this.props;
    const maxDate = this.getMaxDate();

    const onChangeWrapper = (date, index) => {
      try {
        const formatted = format(date, 'yyyyMMdd');

        onChangeDate(formatted, index);
      } catch {
        console.error('Invalid date in input:', date);
      }
    };

    return (
      <DatePicker
        className={`datepicker entry-date${maxDate ? ' bg-danger' : ''}`}
        selected={parse(entry.date, 'yyyyMMdd', new Date())}
        onChange={date => onChangeWrapper(date, index)}
        placeholderText="Start date"
        dateFormat="dd MMM yyyy"
        maxDate={maxDate}
        disabled={!Writer}
      />
    );
  };

  getTime = (entry, index) => {
    const {
      auth: {
        user: {
          permissions: { Writer },
        },
      },
      onChangeHour,
      onChangeMinute,
    } = this.props;
    const hours = [];
    const mins = [];

    for (let i = 0; i < 60; i += 1) {
      if (i < 24) {
        hours.push(<option key={i}>{i < 10 ? `0${i}` : i}</option>);
      }

      mins.push(<option key={i}>{i < 10 ? `0${i}` : i}</option>);
    }

    return (
      <div className="w-[100%] flex justify-center">
        <div className="flex items-center max-w-fit group">
          <div
            className={`
    flex items-center px-3 py-2 space-x-2 transition-all duration-200 bg-white border add-shadow rounded-xl
    ${!Writer ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-white border-slate-200 shadow-sm hover:border-orange-400 focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-orange-500'}
  `}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`w-4 h-4 ${!Writer ? 'text-gray-400' : 'text-slate-400'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex items-center">
              <select
                value={entry.time.slice(0, 2)}
                onChange={event => onChangeHour(event, index)}
                disabled={!Writer}
                className=" p-1  hover:scale-x-110 duration-200 bg-transparent font-medium text-slate-700 text-sm focus:outline-none cursor-pointer disabled:cursor-not-allowed"
                data-test={`campaign-schedule-hour-select-${index}`}
              >
                {hours}
              </select>

              <span className="mx-1 font-bold text-slate-300">:</span>

              <select
                value={entry.time.slice(2, 4)}
                onChange={event => onChangeMinute(event, index)}
                disabled={!Writer}
                className=" bg-transparent p-1  hover:scale-x-110 font-medium text-slate-700 text-sm focus:outline-none cursor-pointer disabled:cursor-not-allowed"
                data-test={`campaign-schedule-minute-select-${index}`}
              >
                {mins}
              </select>
            </div>
          </div>
        </div>
      </div>
    );
  };

  getRemove = (entry, index) => {
    const { data, onRemoveEntry } = this.props;
    let removeButton = '';

    if (data.length > 1) {
      removeButton = (
        <ToolTipIcon toolText="Remove" onClick={() => onRemoveEntry(index)}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 31 41"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="hover:!text-red-500 duration-300 !text-dark-night "
          >
            <path
              d="M2.17919 36.1987C2.17919 38.6849 4.15407 40.7302 6.55458 40.7302H24.0391C26.4397 40.7302 28.4145 38.6849 28.4145 36.1987V9.04527H2.17919V36.1987ZM9.10832 21.7051C8.25707 20.8235 8.25707 19.3953 9.10832 18.5137C9.95956 17.6321 11.3386 17.6321 12.1898 18.5137L15.2884 21.7051L18.3699 18.5137C19.2211 17.6321 20.6001 17.6321 21.4514 18.5137C22.3026 19.3953 22.3026 20.8235 21.4514 21.7051L18.3699 24.8965L21.4514 28.088C22.3026 28.9696 22.3026 30.3978 21.4514 31.2794C20.6001 32.161 19.2211 32.161 18.3699 31.2794L15.2884 28.088L12.2068 31.2794C11.3556 32.161 9.97659 32.161 9.12534 31.2794C8.2741 30.3978 8.2741 28.9696 9.12534 28.088L12.2068 24.8965L9.10832 21.7051ZM28.3975 2.25691H22.9325L21.3833 0.652388C20.9917 0.24685 20.4299 0 19.851 0H10.7257C10.1639 0 9.58501 0.24685 9.19344 0.652388L7.64418 2.25691H2.17919C0.970419 2.25691 0 3.27957 0 4.51382C0 5.74807 0.987444 6.77073 2.17919 6.77073H28.3975C29.6063 6.77073 30.5767 5.74807 30.5767 4.51382C30.5767 3.27957 29.5893 2.25691 28.3975 2.25691Z"
              fill="currentColor"
            />
          </svg>
        </ToolTipIcon>
      );
    }

    return removeButton;
  };

  getViewCampaign = entry => {
    return (
      <Link
        to={`/campaigns/${entry.campaignId}`}
        onClick={this.update}
        className="!rounded-[15px] w-[59px] px-4 p-2 hover:!text-white !appearance-none !no-underline !cursor-pointer !h-[26px] !bg-btn-primary-form !text-white poppins-bold !font-[500]"
      >
        View
      </Link>
    );
  };

  render() {
    const {
      auth: {
        user: {
          permissions: { Writer },
        },
      },
      data,
    } = this.props;

    if (!data) {
      return null;
    }

    return (
      <SSTable
        className="responsive table-campaign-schedule"
        data={data}
        headerHeight={50}
        rowHeight={50}
        rowsCount={data.length - 1}
        zebra
      >
        <SSColumn center width={5} cell={this.getPosition} />
        <SSColumn width={15} header="Name" cell={this.getName} />
        <SSColumn width={30} header="Description" cell={this.getDescription} />
        <SSColumn center width={10} header="Start Date" cell={this.getDate} />
        <SSColumn center width={10} header="Start Time" cell={this.getTime} />
        <SSColumn center width={7} header="" cell={this.getViewCampaign} />
        {Writer && <SSColumn center width={5} header="" cell={this.getRemove} />}
      </SSTable>
    );
  }
}

CampaignScheduleEntriesTable.propTypes = {
  auth: PropTypes.object.isRequired,
  data: PropTypes.array,
  onChangeDate: PropTypes.func.isRequired,
  onChangeHour: PropTypes.func.isRequired,
  onChangeMinute: PropTypes.func.isRequired,
  onRemoveEntry: PropTypes.func.isRequired,
};
